import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, User, Plus, Pencil, Trash2, Undo2, RefreshCw, X, Inbox } from 'lucide-react';
import { API_ENDPOINTS } from '../../../config/api';
import SortFilterControl, { SORT_FIELDS } from '../../../components/ui/SortFilterControl';
import PendingAccounts from './PendingAccounts';

/**
 * "Add / Remove / Edit an Account or Group" — Manual section widget.
 *
 * Postgres-only CRUD over client_groups / client_group_members (Groups tab) and
 * clients / client_account_mapping (Accounts tab). The data lake is never written
 * here. Backend: /api/manual/groups* and /api/manual/accounts*.
 * See docs/superpowers/specs/2026-06-24-report-groups-design.md (Component D).
 */
const AccountGroupManager = ({ onBack, fetchWithAuth, darkMode }) => {
  const [tab, setTab] = useState('accounts'); // 'accounts' | 'groups' | 'pending'
  // Held here (not inside PendingAccounts) because the badge lives in this
  // component's own tab-bar markup, not in PendingAccounts's tree. null means
  // the feed check failed - the count is unknown, not zero, so it renders a
  // "!" marker rather than being treated like 0 (see PendingAccounts).
  //
  // PendingAccounts is mounted unconditionally below (visibility toggled with
  // a CSS class, not conditional rendering) so its count-fetch effect runs as
  // soon as this manager renders, not only once the PM clicks the Pending
  // tab. A badge that requires visiting the tab it warns about defeats the
  // point of having a badge.
  const [pendingCount, setPendingCount] = useState(0);
  const pendingBadgeLabel = pendingCount === null
    ? 'Pending count unknown - the feed check failed'
    : `${pendingCount} pending account${pendingCount === 1 ? '' : 's'}`;
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <button onClick={onBack} className={`text-sm ${muted} hover:underline`}>← Manual</button>
          <h4 className="text-xl font-semibold mt-1">Add / Remove / Edit an Account or Group</h4>
          <p className={`text-sm ${muted}`}>Manage report groups and accounts. Changes are saved to the app database.</p>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        {[
          { k: 'accounts', label: 'Accounts', icon: User },
          { k: 'groups', label: 'Groups', icon: Users },
          { k: 'pending', label: 'Pending', icon: Inbox },
        ].map((t) => (
          <button
            key={t.k}
            data-testid="manual-tab"
            onClick={() => setTab(t.k)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border ${
              tab === t.k
                ? 'bg-teal-600 text-white border-teal-600'
                : darkMode ? 'border-white/10 text-gray-300' : 'border-gray-300 text-gray-700'
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
            {t.k === 'pending' && (pendingCount === null || pendingCount > 0) && (
              <span
                className={`px-1.5 py-0.5 rounded-full text-xs leading-none text-white ${
                  pendingCount === null ? 'bg-amber-600' : 'bg-teal-800'
                }`}
                title={pendingBadgeLabel}
                aria-label={pendingBadgeLabel}
              >
                {pendingCount === null ? '!' : pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'groups' && <GroupsPanel fetchWithAuth={fetchWithAuth} darkMode={darkMode} />}
      {tab === 'accounts' && <AccountsPanel fetchWithAuth={fetchWithAuth} darkMode={darkMode} />}
      {/* Always mounted (never conditionally unmounted like the other two
          panels) so its pending-count fetch fires on first render of this
          manager. Visibility is toggled with a CSS class instead of JSX
          conditional rendering, which keeps this a single mount for the
          manager's lifetime - one fetch on load, not one per tab visit. */}
      <div className={tab === 'pending' ? '' : 'hidden'}>
        <PendingAccounts
          fetchWithAuth={fetchWithAuth}
          darkMode={darkMode}
          onCountChange={setPendingCount}
        />
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------
// Shared bits
// --------------------------------------------------------------------------

// The Manual tab is the only surface that shows soft-deleted rows (greyed, with
// a Revert button), so it opts in explicitly. Every other consumer - the report
// picker, the chatbot, prewarm - keeps seeing live rows only.
const INCLUDE_DELETED = '?include_deleted=true';

/**
 * Soft-delete (DELETE) or restore (POST .../restore) a group or an account.
 *
 * Both entities share the shape, so this is the single place that knows the
 * wire contract. The restore path is derived from the entity URL because
 * config/api.js has no groupRestore/accountRestore entry yet - repoint here
 * once it does. DELETE is soft server-side: the row comes back from the list
 * endpoints carrying deleted_at, which is what keeps it visible but greyed.
 */
const softDelete = async (fetchWithAuth, entityUrl, { restore = false } = {}) => {
  const res = await fetchWithAuth(restore ? `${entityUrl}/restore` : entityUrl, {
    method: restore ? 'POST' : 'DELETE',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `${restore ? 'Revert' : 'Delete'} failed (${res.status})`);
  }
};

const applySort = (rows, { field, dir }, keyOf) => {
  const sign = dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = keyOf(a, field);
    const bv = keyOf(b, field);
    return av === bv ? 0 : (av < bv ? -sign : sign);
  });
};

const ALPHA_ASC = { field: SORT_FIELDS.ALPHA, dir: 'asc' };

const ConfirmModal = ({ open, title, body, onConfirm, onCancel, darkMode }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;
  const card = darkMode ? 'bg-[#1a1a1a] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900';
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className={`w-full max-w-sm rounded-lg border p-6 shadow-xl ${card}`}
      >
        <h5 className="font-semibold mb-2">{title}</h5>
        <p className={`text-sm mb-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{body}</p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className={`px-3 py-1.5 rounded border text-sm ${darkMode ? 'border-white/10' : 'border-gray-300'}`}
          >
            Cancel
          </button>
          <button onClick={onConfirm} className="px-3 py-1.5 rounded bg-red-600 text-white text-sm">Confirm</button>
        </div>
      </div>
    </div>
  );
};

/**
 * Edit / Delete / Revert cluster, shared by both panels' rows.
 *
 * A deleted row renders ONLY Revert - that is what makes it non-interactive.
 * Deliberately not `pointer-events-none` on the row: Revert lives inside the
 * row, so disabling the subtree would disable the way back out of it.
 */
const RowActions = ({ editing, deleted, muted, onEdit, onDelete, onRevert, onSave, onCancel }) => {
  if (editing) {
    return (
      <>
        <button onClick={onSave} className="px-2 py-0.5 rounded bg-teal-600 text-white text-xs">Save</button>
        <button onClick={onCancel} className={`text-sm ${muted} hover:underline`}>Cancel</button>
      </>
    );
  }
  if (deleted) {
    return (
      <button onClick={onRevert} className={`flex items-center gap-1 text-sm ${muted} hover:underline`}>
        <Undo2 className="w-4 h-4" /> Revert
      </button>
    );
  }
  return (
    <>
      <button onClick={onEdit} aria-label="Edit" className={`${muted} hover:text-teal-500`}><Pencil className="w-4 h-4" /></button>
      <button onClick={onDelete} aria-label="Delete" className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
    </>
  );
};

// --------------------------------------------------------------------------
// Groups
// --------------------------------------------------------------------------

const GROUP_SORT_FIELDS = [
  { key: SORT_FIELDS.MARKET_VALUE, label: 'Market value' },
  { key: SORT_FIELDS.ACCOUNT_COUNT, label: 'Number of accounts' },
  { key: SORT_FIELDS.INCEPTION, label: 'Inception date' },
  { key: SORT_FIELDS.ACTIVITY, label: 'Active / Inactive' },
  { key: SORT_FIELDS.ALPHA, label: 'Alphabetical' },
];

// GroupOut carries identity + membership only - no market value and no
// inception date - so those two keys have nothing to order by and fall through
// to alphabetical rather than shuffling the list at random. They stay offered
// because the backend is expected to grow them.
const groupSortKey = (g, field) => {
  if (field === SORT_FIELDS.ACCOUNT_COUNT) return g.member_count ?? 0;
  if (field === SORT_FIELDS.ACTIVITY) return g.deleted_at ? 1 : 0;
  return (g.name || g.codename || '').toUpperCase();
};

const GroupsPanel = ({ fetchWithAuth, darkMode }) => {
  const [groups, setGroups] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState(ALPHA_ASC);
  const [editing, setEditing] = useState(null); // { id, name }
  const [confirming, setConfirming] = useState(null); // the group awaiting delete confirmation

  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const card = darkMode ? 'bg-[#1a1a1a] border-white/10' : 'bg-white border-gray-200';
  const input = `px-2 py-1 rounded border text-sm ${darkMode ? 'bg-[#111] border-white/10 text-white' : 'bg-white border-gray-300'}`;

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [gr, ac] = await Promise.all([
        fetchWithAuth(API_ENDPOINTS.manual.groups + INCLUDE_DELETED),
        fetchWithAuth(API_ENDPOINTS.manual.accounts),
      ]);
      if (!gr.ok) throw new Error(`Groups failed (${gr.status})`);
      if (!ac.ok) throw new Error(`Accounts failed (${ac.status})`);
      setGroups(await gr.json());
      setAccounts(await ac.json());
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);
  useEffect(() => { load(); }, [load]);

  const createGroup = async () => {
    if (!newCode.trim()) { setError('Codename is required'); return; }
    setError(null); setNotice(null);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.manual.groups, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codename: newCode.trim(), name: newName.trim() || null }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Create failed (${res.status})`);
      setNewCode(''); setNewName(''); setNotice('Group created'); load();
    } catch (e) { setError(e.message); }
  };

  const saveEdit = async () => {
    setError(null); setNotice(null);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.manual.group(editing.id), {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editing.name.trim() || null }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Save failed (${res.status})`);
      setEditing(null); setNotice('Group updated'); load();
    } catch (e) { setError(e.message); }
  };

  // Refetch rather than mutate locally, so the greyed state always reflects the
  // server - the row is never greyed on a delete that silently failed.
  const setDeleted = async (g, restore) => {
    setConfirming(null); setError(null); setNotice(null);
    try {
      await softDelete(fetchWithAuth, API_ENDPOINTS.manual.group(g.id), { restore });
      setNotice(restore ? `Restored @${g.codename}` : `Deleted @${g.codename} - use Revert to bring it back`);
      load();
    } catch (e) { setError(e.message); }
  };

  const addMember = async (g, clientId) => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.manual.groupMembers(g.id), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_ids: [clientId] }),
      });
      if (!res.ok) throw new Error(`Add failed (${res.status})`);
      load();
    } catch (e) { setError(e.message); }
  };

  const removeMember = async (g, clientId) => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.manual.groupMember(g.id, clientId), { method: 'DELETE' });
      if (!res.ok) throw new Error(`Remove failed (${res.status})`);
      load();
    } catch (e) { setError(e.message); }
  };

  // Member account numbers are already on every GroupOut, so matching them
  // costs no extra request - a PM who knows the account but not the @name can
  // still find the group that holds it.
  const shown = useMemo(() => {
    const lt = q.trim().toLowerCase();
    const matched = !lt ? groups : groups.filter((g) =>
      (g.codename || '').toLowerCase().includes(lt)
      || (g.name || '').toLowerCase().includes(lt)
      || (g.members || []).some((m) => (m.account_numbers || []).some((n) => (n || '').toLowerCase().includes(lt))));
    return applySort(matched, sort, groupSortKey);
  }, [groups, q, sort]);

  return (
    <div>
      {error && <div className="mb-3 px-3 py-2 rounded bg-red-100 text-red-800 text-sm">{error}</div>}
      {notice && <div className="mb-3 px-3 py-2 rounded bg-green-100 text-green-800 text-sm">{notice}</div>}

      {/* New group */}
      <div className={`flex flex-wrap items-end gap-2 p-3 rounded-lg border mb-4 ${card}`}>
        <div>
          <label className={`block text-xs ${muted}`}>Codename (the @name)</label>
          <input className={input} value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="TRADING" />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className={`block text-xs ${muted}`}>Display name (optional)</label>
          <input className={`${input} w-full`} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Trading Accounts" />
        </div>
        <button onClick={createGroup} className="flex items-center gap-1 px-3 py-1.5 rounded bg-teal-600 text-white text-sm">
          <Plus className="w-4 h-4" /> Create group
        </button>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <input className={`${input} w-64`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search groups…" />
        <button onClick={load} className={`flex items-center gap-1 text-sm ${muted} hover:underline`}><RefreshCw className="w-4 h-4" /> Refresh</button>
        <SortFilterControl value={sort} onChange={setSort} fields={GROUP_SORT_FIELDS} darkMode={darkMode} />
        <span className={`text-sm ${muted}`}>{shown.length} groups</span>
      </div>

      {loading ? <p className={muted}>Loading…</p> : groups.length === 0 ? (
        <p className={muted}>No groups yet. Create one above.</p>
      ) : shown.length === 0 ? (
        <p className={muted}>No groups match that search.</p>
      ) : (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {shown.map((g) => (
            <div
              key={g.id}
              data-testid={`group-row-${g.id}`}
              className={`rounded-lg border ${card} ${g.deleted_at ? 'opacity-60' : ''}`}
            >
              <div className="flex items-center gap-2 px-3 py-2">
                <Users className="w-4 h-4 text-purple-500" />
                <span className="font-medium text-purple-500">@{g.codename}</span>
                {editing?.id === g.id ? (
                  <input
                    autoFocus
                    aria-label={`Display name for @${g.codename}`}
                    className={`${input} flex-1 min-w-[120px]`}
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  />
                ) : g.name && <span className={`text-sm ${muted}`}>— {g.name}</span>}
                <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>{g.member_count} members</span>
                <div className="ml-auto flex items-center gap-2">
                  {!g.deleted_at && editing?.id !== g.id && (
                    <button onClick={() => setExpanded(expanded === g.id ? null : g.id)} className={`text-sm ${muted} hover:underline`}>
                      {expanded === g.id ? 'Hide' : 'Members'}
                    </button>
                  )}
                  <RowActions
                    editing={editing?.id === g.id}
                    deleted={!!g.deleted_at}
                    muted={muted}
                    onEdit={() => setEditing({ id: g.id, name: g.name || '' })}
                    onDelete={() => setConfirming(g)}
                    onRevert={() => setDeleted(g, true)}
                    onSave={saveEdit}
                    onCancel={() => setEditing(null)}
                  />
                </div>
              </div>
              {expanded === g.id && !g.deleted_at && (
                <GroupMembersEditor
                  group={g} accounts={accounts} darkMode={darkMode}
                  onAdd={(cid) => addMember(g, cid)} onRemove={(cid) => removeMember(g, cid)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        open={!!confirming}
        title="Delete this group?"
        body={confirming
          ? `@${confirming.codename} and its ${confirming.member_count} members will be hidden. Nothing is destroyed - the row stays listed with a Revert button.`
          : ''}
        onConfirm={() => setDeleted(confirming, false)}
        onCancel={() => setConfirming(null)}
        darkMode={darkMode}
      />
    </div>
  );
};

const GroupMembersEditor = ({ group, accounts, darkMode, onAdd, onRemove }) => {
  const [q, setQ] = useState('');
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const input = `px-2 py-1 rounded border text-sm w-full ${darkMode ? 'bg-[#111] border-white/10 text-white' : 'bg-white border-gray-300'}`;
  const memberIds = useMemo(() => new Set((group.members || []).map((m) => m.client_id)), [group.members]);
  const candidates = useMemo(() => {
    const lt = q.trim().toLowerCase();
    return accounts
      .filter((a) => !memberIds.has(a.client_id))
      .filter((a) => !lt || (a.name || '').toLowerCase().includes(lt) || (a.account_number || '').toLowerCase().includes(lt))
      .slice(0, 8);
  }, [accounts, memberIds, q]);

  return (
    <div className="px-3 pb-3 border-t border-gray-200 dark:border-white/10 pt-3 grid md:grid-cols-2 gap-4">
      <div>
        <div className={`text-xs font-semibold uppercase ${muted} mb-1`}>Members ({(group.members || []).length})</div>
        <div className="space-y-1 max-h-44 overflow-auto">
          {(group.members || []).map((m) => (
            <div key={m.client_id} className="flex items-center gap-2 text-sm">
              <span className="truncate">{m.name || m.codename}</span>
              <span className={`ml-auto font-mono text-xs ${muted}`}>{(m.account_numbers || [])[0] || ''}</span>
              <button onClick={() => onRemove(m.client_id)} className="text-red-500"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {(group.members || []).length === 0 && <p className={`text-sm ${muted}`}>No members.</p>}
        </div>
      </div>
      <div>
        <div className={`text-xs font-semibold uppercase ${muted} mb-1`}>Add account</div>
        <input className={input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search accounts…" />
        <div className="space-y-1 mt-1 max-h-36 overflow-auto">
          {candidates.map((a) => (
            <button key={a.client_id} onClick={() => onAdd(a.client_id)} className="flex items-center gap-2 w-full text-left text-sm hover:bg-teal-500 hover:text-white px-1 py-0.5 rounded">
              <Plus className="w-3.5 h-3.5" />
              <span className="truncate">{a.name || a.codename}</span>
              <span className="ml-auto font-mono text-xs opacity-60">{a.account_number}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------
// Accounts
// --------------------------------------------------------------------------

// Same offer as Groups minus "Number of accounts" - an account is one account.
const ACCOUNT_SORT_FIELDS = GROUP_SORT_FIELDS.filter((f) => f.key !== SORT_FIELDS.ACCOUNT_COUNT);

const accountSortKey = (a, field) => {
  if (field === SORT_FIELDS.ACTIVITY) return a.deleted_at || !a.is_active ? 1 : 0;
  return (a.name || a.account_number || '').toUpperCase();
};

const AccountsPanel = ({ fetchWithAuth, darkMode }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState(ALPHA_ASC);
  const [form, setForm] = useState({ name: '', account_number: '', apx_portfolio_id: '' });
  const [editing, setEditing] = useState(null); // { id, name }
  const [confirming, setConfirming] = useState(null); // the account awaiting delete confirmation

  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const card = darkMode ? 'bg-[#1a1a1a] border-white/10' : 'bg-white border-gray-200';
  const input = `px-2 py-1 rounded border text-sm ${darkMode ? 'bg-[#111] border-white/10 text-white' : 'bg-white border-gray-300'}`;
  const rowB = darkMode ? 'border-white/10' : 'border-gray-200';

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.manual.accounts + INCLUDE_DELETED);
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      setAccounts(await res.json());
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [fetchWithAuth]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim() || !form.account_number.trim()) { setError('Name and account number are required'); return; }
    setError(null); setNotice(null);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.manual.accounts, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(), account_number: form.account_number.trim(),
          apx_portfolio_id: form.apx_portfolio_id ? Number(form.apx_portfolio_id) : null,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Create failed (${res.status})`);
      setForm({ name: '', account_number: '', apx_portfolio_id: '' }); setNotice('Account created'); load();
    } catch (e) { setError(e.message); }
  };

  // Only the name is editable: update_account ignores apx_portfolio_id outright
  // and never accepts a codename, so exposing either would take keystrokes,
  // return 200, and change nothing.
  const saveEdit = async () => {
    setError(null); setNotice(null);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.manual.account(editing.id), {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editing.name.trim() }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Save failed (${res.status})`);
      setEditing(null); setNotice('Account updated'); load();
    } catch (e) { setError(e.message); }
  };

  const toggleActive = async (a) => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.manual.account(a.client_id), {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !a.is_active }),
      });
      if (!res.ok) throw new Error(`Update failed (${res.status})`);
      load();
    } catch (e) { setError(e.message); }
  };

  const setDeleted = async (a, restore) => {
    setConfirming(null); setError(null); setNotice(null);
    try {
      await softDelete(fetchWithAuth, API_ENDPOINTS.manual.account(a.client_id), { restore });
      setNotice(restore ? `Restored ${a.name}` : `Deleted ${a.name} - use Revert to bring it back`);
      load();
    } catch (e) { setError(e.message); }
  };

  const shown = useMemo(() => {
    const lt = q.trim().toLowerCase();
    const matched = accounts.filter((a) => !lt || (a.name || '').toLowerCase().includes(lt) || (a.account_number || '').toLowerCase().includes(lt));
    return applySort(matched, sort, accountSortKey);
  }, [accounts, q, sort]);

  return (
    <div>
      {error && <div className="mb-3 px-3 py-2 rounded bg-red-100 text-red-800 text-sm">{error}</div>}
      {notice && <div className="mb-3 px-3 py-2 rounded bg-green-100 text-green-800 text-sm">{notice}</div>}

      <div className={`flex flex-wrap items-end gap-2 p-3 rounded-lg border mb-4 ${card}`}>
        <div className="flex-1 min-w-[160px]">
          <label className={`block text-xs ${muted}`}>Account name</label>
          <input className={`${input} w-full`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="SELBY KIDS TR" />
        </div>
        <div>
          <label className={`block text-xs ${muted}`}>Account number</label>
          <input className={input} value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} placeholder="EQY001234" />
        </div>
        <div>
          <label className={`block text-xs ${muted}`}>APX portfolio id</label>
          <input className={input} value={form.apx_portfolio_id} onChange={(e) => setForm({ ...form, apx_portfolio_id: e.target.value })} placeholder="1864" />
        </div>
        <button onClick={create} className="flex items-center gap-1 px-3 py-1.5 rounded bg-teal-600 text-white text-sm"><Plus className="w-4 h-4" /> Add account</button>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <input className={`${input} w-64`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search accounts…" />
        <button onClick={load} className={`flex items-center gap-1 text-sm ${muted} hover:underline`}><RefreshCw className="w-4 h-4" /> Refresh</button>
        <SortFilterControl value={sort} onChange={setSort} fields={ACCOUNT_SORT_FIELDS} darkMode={darkMode} />
        <span className={`text-sm ${muted}`}>{shown.length} accounts</span>
      </div>

      {loading ? <p className={muted}>Loading…</p> : (
        <div className="overflow-x-auto max-h-[60vh]">
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left border-b ${rowB} ${muted}`}>
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Account #</th>
                <th className="py-2 pr-4">APX pid</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4" />
              </tr>
            </thead>
            <tbody>
              {shown.map((a) => (
                <tr
                  key={a.client_id}
                  data-testid={`account-row-${a.client_id}`}
                  className={`border-b ${rowB} ${a.deleted_at ? 'opacity-60' : ''}`}
                >
                  <td className="py-2 pr-4">
                    {editing?.id === a.client_id ? (
                      <input
                        autoFocus
                        aria-label={`Name for ${a.account_number}`}
                        className={`${input} w-full`}
                        value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      />
                    ) : a.name}
                  </td>
                  <td className="py-2 pr-4 font-mono">{a.account_number}</td>
                  <td className="py-2 pr-4 font-mono">{a.apx_portfolio_id ?? '—'}</td>
                  <td className="py-2 pr-4">
                    {/* Deleted is a distinct state from Inactive - the toggle
                        goes read-only so the only way back is Revert. */}
                    <button
                      disabled={!!a.deleted_at}
                      onClick={() => toggleActive(a)}
                      className={`px-2 py-0.5 rounded text-xs disabled:cursor-default ${a.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}
                    >
                      {a.deleted_at ? 'Deleted' : a.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-2">
                      <RowActions
                        editing={editing?.id === a.client_id}
                        deleted={!!a.deleted_at}
                        muted={muted}
                        onEdit={() => setEditing({ id: a.client_id, name: a.name || '' })}
                        onDelete={() => setConfirming(a)}
                        onRevert={() => setDeleted(a, true)}
                        onSave={saveEdit}
                        onCancel={() => setEditing(null)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        open={!!confirming}
        title="Delete this account?"
        body={confirming
          ? `${confirming.name} (${confirming.account_number}) will be hidden from reports. Nothing is destroyed - the row stays listed with a Revert button.`
          : ''}
        onConfirm={() => setDeleted(confirming, false)}
        onCancel={() => setConfirming(null)}
        darkMode={darkMode}
      />
    </div>
  );
};

export default AccountGroupManager;
